import type { ComponentProps, HTMLAttributes } from "react";
import type { Components, ExtraProps } from "react-markdown";

import type { ResolvedStorageKeyPayload } from "src/reusable/context/FocusedTagContext";
import type { CiteGroupPosition } from "src/views/assistant/rehype/rehypeWrapCite";

import CitationLink from "./CitationLink";
import StorageKeyLink from "./StorageKeyLink";

type CitationLinkProps = ComponentProps<typeof CitationLink>;
type MarkdownElementProps = HTMLAttributes<HTMLElement> & ExtraProps;

export interface CitationComponentOptions {
  citationsLoading?: boolean;
  chunkMap?: CitationLinkProps["chunkMap"];
  messageId?: string;
  /**
   * Whether an unmatched cite may lazy-fetch its chunk. Chat does: its
   * `useCitationSources` resolves the registered ids against its dialogue.
   * A surface with no such resolver must not register them, or every chat
   * mounted beside it fetches ids from outside its own dialogue.
   */
  lazyFetch?: boolean;
}

/**
 * The react-markdown renderers for the `<cite>` / `<storage-key>` elements
 * that `rehypeWrapCite` / `rehypeWrapStorageKey` annotate. The property names
 * read here are the contract with those plugins; every surface that renders
 * cited markdown uses this map so a new property lands everywhere at once.
 */
export const makeCitationComponents = ({
  citationsLoading,
  chunkMap,
  messageId,
  lazyFetch = true,
}: CitationComponentOptions): Partial<Components> => ({
  cite: ({ node }: MarkdownElementProps) => {
    const properties = node?.properties ?? {};
    // Null/undefined = an unmatched cite — render it unnumbered rather
    // than mislabeling it [1] (#20423 audit).
    const rawCitationNumber = properties["citationNumber"];
    const sourceData = properties["sourceData"] as unknown as
      | CitationLinkProps["sourceData"]
      | undefined;
    // CitationLink doesn't render children, so we don't pass them.
    // The raw JSON is preserved in the message for agent work.
    return (
      <CitationLink
        citationNumber={
          rawCitationNumber == null ? null : Number(rawCitationNumber)
        }
        hasSource={Boolean(properties["hasSource"])}
        sourceData={
          !lazyFetch && sourceData?.needsFetch
            ? { ...sourceData, needsFetch: false }
            : sourceData
        }
        chunkMap={chunkMap}
        citationsLoading={citationsLoading}
        messageId={messageId}
        citeGroupPosition={
          properties["citeGroupPosition"] as CiteGroupPosition | undefined
        }
      >
        <></>
      </CitationLink>
    );
  },
  // Time Series toolkit sensor citations. Click behavior comes from the
  // FocusedTagContext active in the consuming page tree (default no-op
  // outside Equipment AI).
  ["storage-key" as keyof Components]: ({ node }: MarkdownElementProps) => {
    const properties = node?.properties ?? {};
    return (
      <StorageKeyLink
        payload={
          (properties["payload"] as unknown as ResolvedStorageKeyPayload) ??
          null
        }
        rawBody={(properties["rawBody"] as string | undefined) ?? ""}
      />
    );
  },
});
